import { DateSearch } from '@/types/jobs';
import { parseAndFormatDate } from '@/utils/dateHelper';
import { clearScrollWhenSearchCriteriaHasChanged } from '@/utils/jobSearchAutoScroll';
import DateFnsUtils from '@date-io/date-fns';
import cx from 'classnames';
import React, { FunctionComponent } from 'react';

import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import type { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

import styles from './DatePickers.module.scss';

const formatDate = (date?: MaterialUiPickersDate | string | null): string | null => {
  if (!date) {
    return null;
  }
  const formattedDate = parseAndFormatDate(date, 'YYYY-MM-DD');
  // return 'none' for invalid date to trigger the inline error message
  return formattedDate ? formattedDate : 'none';
};

export type DatePickersProps = {
  /** The startDate and endDate of the current search */
  date: DateSearch;
  /** A function that is called when the startDate or endDate are updated. Accepts a parameter of type DateSearch */
  onDateChange: (DateSearch) => void;
  /** If true, disables the endDate date picker */
  isEndDateDisabled: boolean;
  startDateClassname?: string;
  endDateClassname?: string;
};

/**
 * Adds start and end date selection capabilities to the Job Search box.
 * This is a 'heavy' component that should be lazy loaded only when the job search component is configured to actually
 * render the date pickers
 * @param date The startDate and endDate of the current search
 * @param onDateChange A function that is called when the startDate or endDate are updated. Accepts a parameter of type DateSearch
 * @param isEndDateDisabled If true, disables the endDate date picker.
 * @param startDateClassname Layout styles.
 * @param endDateClassname Layout styles.
 * @constructor
 */
const DatePickers: FunctionComponent<DatePickersProps> = ({
  date,
  onDateChange,
  isEndDateDisabled,
  startDateClassname,
  endDateClassname,
}) => {
  const formattedDate = {
    startDate: formatDate(date.startDate),
    endDate: formatDate(date.endDate),
  };
  const variant = 'dialog';
  const clearLabel = 'CLEAR';
  const invalidDateMessage = 'Invalid date';
  const format = 'dd.MM.yyyy';
  const inputVariant = 'outlined';
  const className = styles.outlinedPicker;

  const handleStartDateChange = v => {
    onDateChange({
      ...formattedDate,
      startDate: formatDate(v),
    });
    clearScrollWhenSearchCriteriaHasChanged();
  };

  const handleEndDateChange = v => {
    onDateChange({
      ...formattedDate,
      endDate: formatDate(v),
    });
    clearScrollWhenSearchCriteriaHasChanged();
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <div className={cx(styles.container, startDateClassname)}>
        <KeyboardDatePicker
          variant={variant}
          clearable
          clearLabel={clearLabel}
          format={format}
          autoOk
          inputVariant={inputVariant}
          className={className}
          fullWidth
          invalidDateMessage={invalidDateMessage}
          label="Start date"
          value={formattedDate.startDate}
          onChange={handleStartDateChange}
          KeyboardButtonProps={{
            'aria-label': 'change date',
          }}
          inputProps={{
            'data-testid': 'job-search-start-date-input',
          }}
        />
      </div>
      <div className={cx(styles.container, endDateClassname)}>
        <KeyboardDatePicker
          disabled={isEndDateDisabled}
          variant={variant}
          clearable
          clearLabel={clearLabel}
          format={format}
          autoOk
          inputVariant={inputVariant}
          className={className}
          invalidDateMessage={invalidDateMessage}
          fullWidth
          label="End date"
          minDateMessage="The end date cannot be earlier than the start date"
          minDate={formattedDate.startDate}
          disablePast
          value={formattedDate.endDate}
          onChange={handleEndDateChange}
          KeyboardButtonProps={{
            'aria-label': 'change date',
          }}
          inputProps={{
            'data-testid': 'job-search-end-date-input',
          }}
        />
      </div>
    </MuiPickersUtilsProvider>
  );
};

export default DatePickers;
